"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { useResponsive } from "@/app/hooks/useResponsive";

interface PortfolioVideo {
  id: number;
  src: string; 
  poster: string;
  title?: string; 
	description?: string
}

interface PortfolioCarouselProps {
  videos: PortfolioVideo[];
}

export default function PortfolioCarousel({ videos }: PortfolioCarouselProps) {
  const { isLg } = useResponsive();

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    mode: "snap",
    slides: { perView: 1.2, spacing: 16 },
    breakpoints: {
      "(min-width: 768px)": { slides: { perView: 2, spacing: 24 } },
      "(min-width: 1024px)": { slides: { perView: 3, spacing: 32 } },
    },
  });

  useEffect(() => {
    const videoElements =
      document.querySelectorAll<HTMLVideoElement>(".portfolio-video");

    if (videoElements[0]) {
      videoElements[0].play().catch(() => {});
    }
  }, [videos]);

  useEffect(() => {
    const videoElements =
      document.querySelectorAll<HTMLVideoElement>(".portfolio-video");

    const handlePlay = (current: HTMLVideoElement) => {
      videoElements.forEach((video) => {
        if (video !== current) {
          video.pause();
        }
      });
    };

    videoElements.forEach((video) => {
      video.addEventListener("play", () => handlePlay(video));
    });

    return () => {
      videoElements.forEach((video) => {
        video.removeEventListener("play", () => handlePlay(video));
      });
    };
  }, [videos]);


  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const videoElements =
      document.querySelectorAll<HTMLVideoElement>(".portfolio-video");

    videoElements.forEach((video, idx) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && idx === 0) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(video);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, [videos]);

  return (
    <div ref={sliderRef} className="keen-slider">
      {videos.map(({ id, src, poster, title, description }) => (
        <div
          key={id}
          className={
            !isLg
              ? "keen-slider__slide relative rounded-xl overflow-hidden shadow-lg"
              : `relative aspect-[10/16] w-full rounded-xl overflow-hidden shadow-lg flex gap-y-4`
          }
        >
          <motion.video
            className="portfolio-video w-full h-full object-cover"
            poster={poster}
            muted={false}
            controls
            preload="metadata"
            playsInline
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
          >
            <source src={src} type="video/mp4" />
            <source src={src.replace(".mp4", ".webm")} type="video/webm" />´
            <p className="sr-only">Descrição: {description}</p>
          </motion.video>

          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-xl pointer-events-none"></div>

          {title && (
            <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
              {title}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
